<template>
  <div class="flex flex-col p-6">
    <i class="icon-call2 text-32 leading-8 text-accent-yellow mb-5" />
    <h2 class="text-grayscale-96 font-bold leading-130 mb-3">1:1 Video Call</h2>

    <p class="text-13 text-grayscale-90 leading-140 font-medium mb-3">
      Select several times that work for you and
      {{ proName }} will confirm one.
    </p>
    <div
      v-if="!isFullyBooked"
      class="flex items-center gap-2 text-accent-green mb-14"
    >
      <i class="icon-calendar text-16 leading-4" />
      <p class="text-13 leading-140 font-bold">
        Available {{ nextAvailableTime }}
      </p>
    </div>
    <div v-else class="flex items-center gap-2 text-helpers-validation mb-8">
      <i class="icon-info text-16 leading-4" />
      <p class="text-13 leading-140 font-bold">Fully Booked</p>
    </div>
    <div class="flex-1 items-end flex justify-between">
      <p>
        <span class="font-bold text-2xl leading-100 text-grayscale-96"
          >${{ price }}</span
        >
        <span class="inline ml-1.5 text-grayscale-80 leading-100"
          >/ <span class="whitespace-nowrap">Call</span></span
        >
      </p>
      <general-button v-if="!isFullyBooked" height="small" @click="request"
        ><span class="whitespace-nowrap">Book now</span></general-button
      >
    </div>
  </div>
</template>
<script setup lang="ts">
import { useLocalNotificationsStore } from "@/store/notifications";
import { useProAvailabilityStore } from "@/store/pro-availability";
import type { CalendarAvailability } from "@/utils/parse-calendar-availability";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import type { User } from "@/types/user";
import { computed, onMounted, ref } from "vue";
import type { PropType } from "vue";
import { useUserStore } from "../../../store/user";
import GeneralButton from "@/components/layout/buttons/GeneralButton.vue";

const props = defineProps({
  proName: {
    type: String,
    required: true,
  },
  pro: {
    type: Object as PropType<User>,
    required: true,
  },
  price: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["request"]);

const userStore = useUserStore();
const proAvailabilityStore = useProAvailabilityStore();
const localNotificationsStore = useLocalNotificationsStore();
const availability = ref<CalendarAvailability>();
const nextAvailableTime = ref("");
const isFullyBooked = ref(false);
dayjs.extend(weekOfYear);
dayjs.updateLocale("en", { weekStart: 1 });

const request = () => {
  emit("request");
};

const getNextAvailableTime = async () => {
  try {
    availability.value = proAvailabilityStore.availability;
    const start = dayjs(availability.value?.availableDates?.start).format(
      "MM/DD/YYYY",
    );
    const days = ref(
      Object.getOwnPropertyNames(availability.value?.availableHoursPerDay),
    );

    const startIndex = ref();
    if (start) {
      startIndex.value = days.value.indexOf(start.toString());
      days.value = days.value
        .slice(startIndex.value)
        .concat(days.value.slice(0, startIndex.value));
      const minDay = days.value.reduce(
        (min, next) =>
          availability.value?.availableHoursPerDay?.[next] &&
          availability.value?.availableHoursPerDay?.[next].length > 0 &&
          dayjs(next).diff(dayjs(min)) < 0
            ? next
            : min,
        dayjs().add(3, "weeks").format("MM/DD/YYYY"),
      );
      const time = dayjs(
        availability.value?.availableHoursPerDay?.[minDay][0].value,
      );
      const timezone = userStore.userData.timezone ?? dayjs.tz.guess();
      const weeksDiff =
        dayjs(time).tz(timezone).week() - dayjs().tz(timezone).week();
      const connectionWord = weeksDiff === 0 || weeksDiff > 1 ? "from" : "next";
      const dayName =
        weeksDiff > 1
          ? dayjs(time).tz(timezone).format("dddd, MMMM D")
          : dayjs(time).tz(timezone).format("dddd");
      nextAvailableTime.value = `${connectionWord} ${dayName}`;
    }
    isFullyBooked.value =
      (props.pro.consultationCallCount ?? 0) >=
        (props.pro.consultationCallsPerWeek ?? 0) ||
      Object.keys(availability.value?.availableHoursPerDay as any).length === 0;
  } catch (error: any) {
    localNotificationsStore.addNotification({
      message: error.message,
      isError: true,
    });
  }
};

onMounted(() => {
  getNextAvailableTime();
});
</script>
